import { ILineSelectionInfo } from '../types';
import { Logger } from '../../../packages/logger/src';
import { setLocalStorageItem, getLocalStorageItem, removeLocalStorageItem } from '../../../ms-teams-sdk/util/LocalStorageUtil';

/**
 * Lightweight bridge between LineSelector (React state) and CrossLaunchService.
 *
 * Stores the selected line's GUID and 1-based index in localStorage so that
 * makeCrossLaunchCall() can append line identity to the webextel:// URL.
 *
 * Per the protocol handler spec (Confluence: "Protocol Handler — Multi-line Support"),
 * the Webex Desktop app accepts `lineId` (GUID) and `lineIndex` (1-based) in the
 * cross-launch URL to specify which line to use for the call.
 *
 * Consumers (CrossLaunchService, LandingPage) send BOTH `lineIndex` and `lineId`
 * for Phase 1/2 Desktop compatibility. Phase 1 Desktop reads lineIndex only;
 * Phase 2 reads lineId first, falls back to lineIndex.
 *
 * Also used by useLineSelection to restore the user's selection across page
 * loads / tab switches (GUID match preferred, lineIndex as fallback).
 *
 * IMPORTANT: This is only set when the user EXPLICITLY selects a line via the
 * LineSelector UI (selectLine callback). On initial load when no stored selection
 * exists, the cross-launch URL omits line params so the Desktop app uses its own
 * currently selected line. Per the spec: "If the parameter is not provided or
 * incorrect, the current selected line will be used."
 *
 * Ref: https://confluence-eng-gpk2.cisco.com/conf/spaces/WBXT/pages/568760112
 */

const SELECTED_LINE_INDEX_KEY = 'lineSelector_crossLaunchLineIndex';
const SELECTED_LINE_GUID_KEY = 'lineSelector_crossLaunchLineGuid';

/**
 * Persist the selected line index and GUID for cross-launch URL construction.
 * Called from useLineSelection only when the user explicitly selects a line.
 */
export function setSelectedLineForCrossLaunch(line: ILineSelectionInfo | null): void {
  if (!line) {
    removeLocalStorageItem(SELECTED_LINE_INDEX_KEY);
    removeLocalStorageItem(SELECTED_LINE_GUID_KEY);
    return;
  }
  // Protocol handler expects 1-based index; our lineIndex is 0-based
  const oneBasedIndex = line.lineIndex + 1;
  setLocalStorageItem(SELECTED_LINE_INDEX_KEY, String(oneBasedIndex));

  if (line.lineGuid) {
    setLocalStorageItem(SELECTED_LINE_GUID_KEY, line.lineGuid);
  } else {
    removeLocalStorageItem(SELECTED_LINE_GUID_KEY);
  }

  Logger.debug(`[LineSelection] Cross-launch stored: lineIndex=${oneBasedIndex}, lineId=${line.lineGuid || 'N/A'} (${line.lineLabel})`);
}

/**
 * Read the selected line index for cross-launch URL construction.
 * Called from CrossLaunchService when building the webextel:// URL.
 * Returns the 1-based index, or null if no selection is stored.
 */
export function getSelectedLineIndexForCrossLaunch(): number | null {
  const stored = getLocalStorageItem<string>(SELECTED_LINE_INDEX_KEY);
  if (!stored) return null;
  const parsed = parseInt(stored, 10);
  return isNaN(parsed) ? null : parsed;
}

/**
 * Read the selected line GUID for cross-launch URL construction.
 * Returns the GUID string, or null if not available.
 */
export function getSelectedLineGuidForCrossLaunch(): string | null {
  return getLocalStorageItem<string>(SELECTED_LINE_GUID_KEY) || null;
}
